/**
 * Explicit application state machine.
 *
 * Everything the UI shows is a function of `AppState` plus the payload attached to it.
 * There are deliberately no free-floating booleans for "camera ready" / "model loaded" /
 * "xr active" — those are states or state payload, so an impossible combination cannot be
 * represented.
 */

export const APP_STATES = [
  'intro',
  'requestingCamera',
  'loadingModel',
  'placement',
  'calibrating',
  'ready',
  'playing',
  'noHands',
  'permissionDenied',
  'cameraUnsupported',
  'error',
] as const;

export type AppState = (typeof APP_STATES)[number];

/** Classification used to pick recovery affordances and copy. */
export type AppErrorKind =
  | 'camera-permission'
  | 'camera-unavailable'
  | 'camera-facing-unavailable'
  | 'camera-interrupted'
  | 'model-download'
  | 'wasm-init'
  | 'webgl-unavailable'
  | 'audio'
  | 'xr-session'
  | 'unknown';

export interface AppErrorInfo {
  kind: AppErrorKind;
  message: string;
  /** True when the user can retry in place rather than reloading. */
  recoverable: boolean;
  cause?: unknown;
}

export interface AppContext {
  error: AppErrorInfo | null;
  /** Set once the camera and landmarker are both live. */
  instrumentArmed: boolean;
}

type Transitions = Readonly<Record<AppState, readonly AppState[]>>;

/**
 * Allowed transitions. Terminal-ish error states can always be retried back to the start of
 * the acquisition sequence, and any state can fall into `error`.
 */
const TRANSITIONS: Transitions = {
  intro: ['requestingCamera', 'error', 'cameraUnsupported'],
  requestingCamera: ['loadingModel', 'permissionDenied', 'cameraUnsupported', 'error', 'intro'],
  loadingModel: ['placement', 'ready', 'error', 'intro'],
  placement: ['ready', 'calibrating', 'error', 'intro', 'noHands'],
  calibrating: ['ready', 'placement', 'error', 'intro', 'noHands'],
  ready: ['playing', 'noHands', 'placement', 'calibrating', 'intro', 'error'],
  playing: ['ready', 'noHands', 'placement', 'calibrating', 'intro', 'error'],
  noHands: ['ready', 'playing', 'placement', 'calibrating', 'intro', 'error'],
  permissionDenied: ['requestingCamera', 'intro', 'error'],
  cameraUnsupported: ['intro', 'requestingCamera', 'error'],
  error: ['intro', 'requestingCamera', 'loadingModel', 'ready', 'placement'],
};

export interface StateChange {
  from: AppState;
  to: AppState;
  context: Readonly<AppContext>;
}

export type StateListener = (change: StateChange) => void;

export class AppStateMachine {
  private state: AppState = 'intro';

  private readonly ctx: AppContext = { error: null, instrumentArmed: false };

  private readonly listeners = new Set<StateListener>();

  get current(): AppState {
    return this.state;
  }

  get context(): Readonly<AppContext> {
    return this.ctx;
  }

  /** True while the app is in a state where the instrument may produce sound. */
  get isLive(): boolean {
    return this.state === 'playing' || this.state === 'ready' || this.state === 'noHands';
  }

  canTransition(to: AppState): boolean {
    return this.state === to || (TRANSITIONS[this.state] ?? []).includes(to);
  }

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Attempt a transition. Illegal transitions are ignored and reported (they indicate a
   * logic bug, but must never crash a live instrument). Returns true when it happened.
   */
  transition(to: AppState, error: AppErrorInfo | null = null): boolean {
    if (to === this.state) {
      if (error) this.ctx.error = error;
      return false;
    }
    if (!this.canTransition(to)) {
      console.warn(`[state] ignored illegal transition ${this.state} -> ${to}`);
      return false;
    }
    const from = this.state;
    this.state = to;
    this.ctx.error = to === 'error' || error ? error : null;
    if (to === 'intro' || to === 'permissionDenied' || to === 'cameraUnsupported') {
      this.ctx.instrumentArmed = false;
    }
    for (const listener of [...this.listeners]) {
      try {
        listener({ from, to, context: this.ctx });
      } catch (err) {
        console.error('[state] listener failed', err);
      }
    }
    return true;
  }

  /** Convenience for error transitions: routes permission/camera errors to their own states. */
  fail(info: AppErrorInfo): void {
    if (info.kind === 'camera-permission') {
      this.transition('permissionDenied', info);
      return;
    }
    if (info.kind === 'camera-unavailable' || info.kind === 'webgl-unavailable') {
      this.transition('cameraUnsupported', info);
      return;
    }
    this.transition('error', info);
  }

  setInstrumentArmed(armed: boolean): void {
    this.ctx.instrumentArmed = armed;
  }

  dispose(): void {
    this.listeners.clear();
  }
}

/** Human-readable copy for each state. Kept next to the machine so they cannot drift. */
export const STATE_COPY: Readonly<Record<AppState, { title: string; body: string }>> = {
  intro: {
    title: 'Virtual Theremin',
    body: 'Play a theremin in the air with your hands. Everything runs on your device.',
  },
  requestingCamera: {
    title: 'Waiting for camera',
    body: 'Allow camera access in your browser to continue.',
  },
  loadingModel: {
    title: 'Loading hand tracking',
    body: 'Preparing the on-device hand-tracking model…',
  },
  placement: {
    title: 'Place the theremin',
    body: 'Drag to move, pinch or scroll to resize, then confirm.',
  },
  calibrating: {
    title: 'Calibration',
    body: 'Follow the prompts to set a comfortable playing range.',
  },
  ready: {
    title: 'Ready',
    body: 'Press Start instrument, then raise both hands.',
  },
  playing: { title: 'Playing', body: 'Right zone controls pitch, left zone controls volume.' },
  noHands: { title: 'No hands detected', body: 'Hold your hands up in front of the camera.' },
  permissionDenied: {
    title: 'Camera permission denied',
    body: 'The instrument needs the camera to see your hands. Grant access and try again.',
  },
  cameraUnsupported: {
    title: 'Camera unavailable',
    body: 'This browser or device did not provide a usable camera stream.',
  },
  error: { title: 'Something went wrong', body: 'You can retry without reloading the page.' },
};
